/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/

import { SandboxCreateTile } from "components/elements/sandbox-tile/SandboxCreateTile";
import { SandboxTiles } from "components/loading-sandbox-list/SandboxTiles";
import { sandboxApi } from "services/sandbox-api/api";
import { CodeShareMetadata } from "types/codeshareTypes";
import { User } from "types/codeshareUser";
import { ExpandableBlock } from "@itwin/itwinui-react";
import styles from "./SandboxList.module.css";

export const SandboxList = ({ user }: { user: User }) => {
  const { data: userCodeshares, isLoading: userCodesharesLoading } = sandboxApi.useCodeSharesQuery({ userId: user.id });
  const { data: likedCodeshares, isLoading: likedCodesharesLoading } = sandboxApi.useCodeSharesLikedQuery({ userId: user.id });
  const { data: featuredCodeshares, isLoading: featuredCodesharesLoading } = sandboxApi.useCodeSharesFeaturedQuery();

  return (
    <div className={styles.container} data-testid="atp-mysandboxes-view">
      {user.isBentleyUser &&
        <ExpandableBlock title="My Sandboxes" styleType="borderless" isExpanded={true} data-testid="atp-user-sandboxes">
          <div className={styles.list}>
            <SandboxCreateTile />
            <SandboxTiles sort={createdSort} isLoading={userCodesharesLoading} list={userCodeshares} showStats={true} />
          </div>
        </ExpandableBlock>
      }
      <ExpandableBlock title="Liked Sandboxes" styleType="borderless" isExpanded={true} data-testid="atp-user-liked-sandboxes">
        <div className={styles.list}>
          <SandboxTiles list={likedCodeshares} isLoading={likedCodesharesLoading} showStats={true} emptyMessage="No Sandboxes available. Like a sandbox for it to appear here" />
        </div>
      </ExpandableBlock>
      <ExpandableBlock title="Featured Sandboxes" styleType="borderless" isExpanded={true} data-testid="atp-featured-sandboxes">
        <div className={styles.list}>
          <SandboxTiles list={featuredCodeshares} isLoading={featuredCodesharesLoading} sort={createdSort} showStats={true} emptyMessage="Featured Sandboxes are not available" />
        </div>
      </ExpandableBlock>
    </div>
  );
};

function createdSort(a: CodeShareMetadata, b: CodeShareMetadata): 1 | -1 | 0 {
  const aa = new Date(Date.parse(a.created));
  const bb = new Date(Date.parse(b.created));

  if (aa < bb) {
    return 1;
  }
  if (aa > bb) {
    return -1;
  }
  return 0;
}
